import React from "react";
import {ButtonLinkStyle} from "../../../../Components/Inputs";
import {getLoanDurationTextByValue, getLoanLibraryMediaFormatTextByValue} from "../loanLibraryConstants";
import {convertDateToPrintString} from "../../../../Utilities/Types/dateUtilities";
import {allow} from "../../../../Components/Authorize";
import * as PolicyEvents from "../../../../Components/Authorize/policyEventsConstants";
import {isRmtcAdmin} from "../../../../Utilities/LocalStorage/storageUtilities";
import {renderCatalogDetail} from "../loanLibraryCatalogUtilities";
import {CatalogItemImage} from "./CatalogItemImage";

const convertToCurrencyFormat = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === "") return "";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? "" : `$${num.toFixed(2)}`;
};

const getAvailabilityText = (quantityCurrentlyAvailable: number | null, totalQuantity: number | null): string => {
    const available = quantityCurrentlyAvailable || 0;
    const total = totalQuantity || 0;

    if (available === 0) return "Unavailable";
    return `${available} of ${total} Available`;
};

interface LoanLibraryCatalogResultsRowProps {
    catalogResource: Record<string, any>;
    canManageCatalog?: boolean;
    handleAddToQueue: (id: string) => void;
    handleEdit: (id: string) => void;
}

export const LoanLibraryCatalogResultsRow = ({
                                                 catalogResource,
                                                 handleAddToQueue,
                                                 handleEdit,
                                             }: LoanLibraryCatalogResultsRowProps) => {

    const handleClickEdit = () => {
        handleEdit(catalogResource.loanLibraryCatalogId);
    };

    const handleClickAddToQueue = () => {
        handleAddToQueue(catalogResource.loanLibraryCatalogId);
    };

    const availabilityText = getAvailabilityText(
        catalogResource.quantityCurrentlyAvailable,
        catalogResource.totalQuantity
    );

    const isAvailable = (catalogResource.quantityCurrentlyAvailable || 0) > 0;

    const formatDate = (dateValue: string | null | undefined) => {
        if (!dateValue) return null;
        return convertDateToPrintString(new Date(dateValue));
    }

    const canManageCatalog = allow(PolicyEvents.MANAGE_LOAN_LIBRARY_CATALOG);

    return (
        <div className="catalog-item" id={catalogResource.catalogNumber}
             data-testid={catalogResource.catalogNumber}>
            <div className="catalog-item__title-row">
                <h3 id={`title${catalogResource.loanLibraryCatalogId}`}>
                    {catalogResource.productName}
                </h3>
                <div className="catalog-item__title-actions">
                    <span className="catalog-item__availability">{availabilityText}</span>
                    <ButtonLinkStyle
                        label={isAvailable ? "Add to Queue" : "Add to Waitlist"}
                        name={`btnAddQueue${catalogResource.loanLibraryCatalogId}`}
                        onClick={handleClickAddToQueue}
                    />
                </div>
            </div>

            <div className="catalog-item__details-row">
                <CatalogItemImage
                    imageUrl={catalogResource.productImageUrl}
                    productName={catalogResource.productName}
                />
                <div className="catalog-item__info">
                    {isRmtcAdmin() && renderCatalogDetail(catalogResource.accessNumber, "Access Number")}
                    {renderCatalogDetail(catalogResource.catalogNumber, "Catalog Number")}
                    {renderCatalogDetail(catalogResource.author, "Author")}
                    {renderCatalogDetail(getLoanLibraryMediaFormatTextByValue(catalogResource.mediaFormat), "Media Format")}
                    {renderCatalogDetail(catalogResource.manufacturer, "Manufacturer")}
                    {renderCatalogDetail(formatDate(catalogResource.publicationDate), "Publication Date")}
                    {isRmtcAdmin() && renderCatalogDetail(formatDate(catalogResource.dateOfPurchase), "Date of Purchase")}
                    {isRmtcAdmin() && renderCatalogDetail(convertToCurrencyFormat(catalogResource.cost), "Cost")}
                    {renderCatalogDetail(catalogResource.recommendedQuantity, "Recommended Quantity")}
                    {renderCatalogDetail(catalogResource.description, "Description", {enableLinkParsing: true})}
                    {isRmtcAdmin() && renderCatalogDetail(catalogResource.keywords, "Keywords")}
                    {renderCatalogDetail(catalogResource.relatedItems, "Related Items", {enableLinkParsing: true})}
                    {renderCatalogDetail(getLoanDurationTextByValue(catalogResource.loanTerms), "Loan Terms")}
                    {
                        canManageCatalog &&
                        <>
                            {renderCatalogDetail(catalogResource.accessoriesAndParts, "Accessories and Parts")}
                            {renderCatalogDetail(catalogResource.vendor, "Vendor")}
                            {renderCatalogDetail(catalogResource.notes, "Notes")}
                            {renderCatalogDetail(catalogResource.isForInternalUse, "Is For Internal Use", {showBooleanOnlyIfTrue: true})}
                        </>
                    }

                    {
                        canManageCatalog &&
                        <div className="catalog-item__edit-link">
                            <ButtonLinkStyle
                                label={"Edit Item"}
                                name={`btnEdit${catalogResource.loanLibraryCatalogId}`}
                                onClick={handleClickEdit}
                            />
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

